import { NavLink, useNavigate } from 'react-router-dom';
import { useTenantStore } from '@/store/tenantStore';
import { useAuthStore } from '@/store/authStore';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { Badge } from '@/components/ui/Badge';
import { Tooltip } from '@/components/ui/Tooltip';

const TOP_NAV = [
  { to: '/dashboard',     label: 'Dashboard',     icon: 'dashboard',     tip: 'Overview of shop performance and key metrics' },
  { to: '/intake',        label: 'Intake',         icon: 'input',         tip: 'Manage incoming vehicle intake requests' },
  { to: '/work-board',    label: 'Work Board',     icon: 'dashboard_customize', tip: 'Kanban board for managing work order status' },
  { to: '/checkin-qr',    label: 'Check-In QR',   icon: 'qr_code',       tip: 'Customer self check-in QR code for your front desk' },
  { to: '/technician-access', label: 'Staff Access QR', icon: 'qr_code_2', tip: 'Secure technician portal QR code for the workshop floor' },
  { to: '/notifications', label: 'Notifications',  icon: 'notifications', tip: 'View alerts, reminders, and system updates' },
] as const;

const MGMT_NAV = [
  // Customers & Business Data
  { to: '/customers',  label: 'Customers',     icon: 'group',          tip: 'View and manage customer profiles' },
  { to: '/vehicles',   label: 'Vehicle Fleet', icon: 'directions_car', tip: 'Track vehicles associated with customers' },

  // Work & Service Orders
  { to: '/workorders', label: 'Work Orders',   icon: 'assignment',     tip: 'Create and manage repair work orders' },
  { to: '/estimates',  label: 'Estimates',     icon: 'request_quote',  tip: 'Create and manage service estimates and quotes' },
  { to: '/appointments', label: 'Appointments', icon: 'calendar_month', tip: 'Schedule and manage service appointments' },

  // Invoicing & Revenue
  { to: '/invoices',   label: 'Invoices',      icon: 'receipt_long',   tip: 'View and send customer invoices' },

  // Operations
  { to: '/inventory',  label: 'Inventory',     icon: 'inventory_2',    tip: 'Manage parts and stock levels' },
  { to: '/broadcast',  label: 'Broadcast',     icon: 'campaign',       tip: 'Send emails to your customers' },
  { to: '/reminders',  label: 'Reminders',     icon: 'notifications_active', tip: 'Automated service reminder rules' },

  // Analytics & Reporting
  { to: '/reports',    label: 'Reports',       icon: 'bar_chart',      tip: 'Business analytics and financial reports' },
  { to: '/performance', label: 'Team Performance', icon: 'monitoring', tip: 'Performance, quality, capacity, coaching and learner development' },

  // Administration
  { to: '/staff',         label: 'Staff',              icon: 'badge',                      tip: 'Manage technicians and staff members' },
  { to: '/integrations',  label: 'Integrations',       icon: 'integration_instructions',   tip: 'Connect accounting and tax apps' },
  { to: '/api-keys',      label: 'API Keys',           icon: 'vpn_key',                    tip: 'Manage API keys for integrations' },
] as const;

function NavItem({ to, label, icon, tip }: { to: string; label: string; icon: string; tip: string }) {
  return (
    <Tooltip text={tip} position="right" className="block">
      <NavLink
        to={to}
        title={tip}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            isActive
              ? 'text-primary font-semibold border-l-4 border-primary'
              : 'text-slate-300 hover:bg-white/10 border-l-4 border-transparent'
          }`
        }
        style={({ isActive }) =>
          isActive ? { backgroundColor: 'rgba(249,115,22,0.2)' } : undefined
        }
      >
        <span className="material-symbols-outlined text-xl">{icon}</span>
        {label}
      </NavLink>
    </Tooltip>
  );
}

function getInitials(name: string) {
  const parts = name.trim().split(' ');
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

export function Sidebar() {
  const { companyName, displayName, planName } = useTenantStore();
  const { staffName, staffRole } = useAuthStore();
  const navigate = useNavigate();
  const { switchUser } = useAuth();
  const permissions = usePermissions();

  const shopLabel    = displayName || companyName || 'WrenchLab';
  const shopInitials = shopLabel.slice(0, 2).toUpperCase();

  // When a real staff session is active, show the staff member's info.
  // For solo shops (synthetic session), staffName is the company name — show role only.
  const hasRealStaffSession = !!staffName && staffName !== shopLabel;
  const primaryLabel   = staffName || shopLabel;
  const avatarInitials = staffName ? getInitials(staffName) : shopInitials;

  // Filter management nav based on permissions
  const visibleMgmtNav = MGMT_NAV.filter((item) => {
    if (item.to === '/staff') return permissions.canManageStaff;
    if (item.to === '/integrations') return permissions.canManageSettings;
    if (item.to === '/api-keys') return permissions.canManageSettings;
    if (item.to === '/broadcast') return permissions.isManagerOrAbove;
    if (item.to === '/reminders') return permissions.isManagerOrAbove;
    if (item.to === '/reports') return permissions.isOwnerOrSolo;
    if (item.to === '/performance') return permissions.isManagerOrAbove;
    return true;
  });
  const visibleTopNav = TOP_NAV.filter((item) => {
    if (item.to === '/intake') return permissions.canManageIntake;
    if (item.to === '/technician-access') return permissions.canManageStaff;
    return true;
  });

  return (
    <aside className="flex flex-col w-64 h-screen bg-sidebar-bg text-white flex-shrink-0">
      {/* Logo */}
      <div className="px-5 py-4 flex items-center gap-3">
        <img
          src="/logo.png"
          alt="WrenchLab"
          className="w-10 h-10 object-contain flex-shrink-0"
        />
        <div>
          <h1 className="font-bold text-lg tracking-tight">WrenchLab</h1>
          <p className="text-xs text-slate-400">Shop Management</p>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto overflow-x-hidden">
        {visibleTopNav.map((item) => (
          <NavItem key={item.to} {...item} />
        ))}

        <div className="pt-4 pb-2 px-3 text-[10px] font-bold uppercase tracking-wider text-slate-400">
          Management
        </div>

        {visibleMgmtNav.map((item) => (
          <NavItem key={item.to} {...item} />
        ))}
      </nav>

      {/* Bottom section */}
      <div className="p-4 border-t border-slate-700 space-y-2">
        {/* User card */}
        <div className="flex items-center gap-3 bg-white/5 p-2 rounded-xl">
          <div className="w-10 h-10 rounded-full bg-primary/30 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold text-primary">{avatarInitials}</span>
          </div>
          <div className="flex-1 overflow-hidden">
            <p className="text-sm font-semibold truncate">{primaryLabel}</p>
            <div className="flex items-center gap-1 mt-0.5">
              <Badge variant={permissions.roleBadgeColor} className="text-xs h-5">
                {permissions.roleLabel}
              </Badge>
            </div>
          </div>

          {/* Switch User — visible when a real staff JWT is active (not solo-shop synthetic) */}
          {hasRealStaffSession && (
            <Tooltip text="Switch user (keeps workspace)" position="top">
              <button
                onClick={switchUser}
                className="text-slate-400 hover:text-white transition-colors flex-shrink-0"
                aria-label="Switch user"
              >
                <span className="material-symbols-outlined text-xl">switch_account</span>
              </button>
            </Tooltip>
          )}

          <Tooltip text="Account settings" position="top">
            <button
              onClick={() => navigate('/settings')}
              className="text-slate-400 hover:text-white transition-colors flex-shrink-0"
              aria-label="Account settings"
            >
              <span className="material-symbols-outlined text-xl">settings</span>
            </button>
          </Tooltip>
          <Tooltip text="Switch user" position="top">
            <button
              onClick={switchUser}
              className="text-slate-400 hover:text-red-400 transition-colors flex-shrink-0"
              aria-label="Switch user"
            >
              <span className="material-symbols-outlined text-xl">logout</span>
            </button>
          </Tooltip>
        </div>
      </div>
    </aside>
  );
}
